'use strict';

import React, { PureComponent } from 'react';
import { Text, View, PixelRatio, Image, TouchableWithoutFeedback } from 'react-native';
import PropTypes from 'prop-types';
import CommonStyles from 'common/CommonStyles';
import { Scaledp } from 'common/Dimens';
import { wrapStyle } from 'utils/computedStyle';
import CommonBridge from 'common/CommonBridge';
import ImageAssets from 'common/ImageAssets';
import System from 'utils/System';
import ImageHandler from './ImageHandler';

const styles = wrapStyle({
  container: {
    height: System.isEmbeddedWebview() ? 0 : 45,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 1 / PixelRatio.get(),
  },
  navBarContainer: {
    height: 45 - 1,
  },
  navBarLeftContainer: {
    paddingLeft: 0,
    paddingRight: 0,
    left: 0,
    height: 45 - 1,
    minWidth: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0)',
    position: 'absolute',
  },
  navBarTitleContainer: {
    height: 45 - 1,
  },
  navBarRightContainer: {
    paddingLeft: 0,
    paddingRight: 0,
    right: 0,
    height: 45 - 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0)',
    position: 'absolute',
  },
  statusBar: {
    height: CommonStyles.statusBarHeight,
    borderBottomWidth: 0,
    backgroundColor: CommonStyles.titleBar.backgroundColor,
  },
});

export default class StdNavBar extends PureComponent {
  static defaultProps = {
    transparent: false,
    showBottomSeparator: true,
  };

  static propTypes = {
    /**
     * nav bar title.
     */
    title: PropTypes.string,
    /**
     * nav bar image title source. (If imageTitle exists, text title will not work)
     */
    imageTitle: PropTypes.object,
    /**
     * search btn callback.
     */
    onBackPress: PropTypes.func,
    /**
     * back btn pressed image.
     */
    leftImagePressed: PropTypes.object,
    /**
     * back btn normal image.
     */
    leftImageNormal: PropTypes.object,
    /**
     * more btn callback.
     */
    onMorePress: PropTypes.func,
    /**
     * more btn pressed image.
     */
    rightImagePressed: PropTypes.object,
    /**
     * more btn normal image.
     */
    rightImageNormal: PropTypes.object,
    /**
     * title bar backgroundColor.
     */
    transparent: PropTypes.bool,

    // render function.
    renderLeftButton: PropTypes.func,
    renderRightButton: PropTypes.func,
    renderTitle: PropTypes.func,
    renderCloseWeb: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {};
  }

  getBackImage(isPressed) {
    if (isPressed) {
      if (this.props.leftImagePressed) {
        return this.props.leftImagePressed;
      } else if (this.props.leftImageNormal) {
        return this.props.leftImageNormal;
      } else {
        return this.getDefaultBackImage(isPressed);
      }
    } else {
      if (this.props.leftImageNormal) {
        return this.props.leftImageNormal;
      } else {
        return this.getDefaultBackImage(isPressed);
      }
    }
  }

  getMoreImage(isPressed) {
    if (isPressed) {
      if (this.props.rightImagePressed) {
        return this.props.rightImagePressed;
      } else if (this.props.rightImageNormal) {
        return this.props.rightImageNormal;
      } else {
        return this.getDefaultMoreImage(isPressed);
      }
    } else {
      if (this.props.rightImageNormal) {
        return this.props.rightImageNormal;
      } else {
        return this.getDefaultMoreImage(isPressed);
      }
    }
  }

  renderLeftButton() {
    if (this.props.renderLeftButton) {
      return this.props.renderLeftButton(this.props.transparent, this.props.useWhiteIcon);
    }

    return (
      <ImageHandler
        source={this.getBackImage(false)}
        useDuplo={false}
        onPress={this.props.onBackPress}
        style={{ width: Scaledp(39), height: Scaledp(48) }}
      />
    );
  }

  renderRightButton() {
    if (this.props.renderRightButton) {
      return this.props.renderRightButton(this.props.transparent, this.props.useWhiteIcon);
    }

    if (!this.props.onMorePress) {
      return null;
    }
    return (
      <ImageHandler
        source={this.getMoreImage(false)}
        useDuplo={false}
        onPress={this.props.onMorePress}
        style={CommonStyles.titleBarButton}
      />
    );
  }

  renderBackgroundImage() {
    if (this.props.renderBackgroundImage) {
      return this.props.renderBackgroundImage();
    }
    return null;
  }

  renderTitle(width) {
    if (this.props.renderTitle && this.props.renderTitle(width, this.props.transparent)) {
      return this.props.renderTitle(width, this.props.transparent);
    }
    if (this.props.imageTitle) {
      return this.renderImageTitle(width);
    }
    return this.renderDefaultTitle(width);
  }

  renderNavbar() {
    if (this.props.renderNavbar) {
      return this.props.renderNavbar(this.props.transparent, this.props.useWhiteIcon);
    }
    const leftBtn = CommonBridge.isWeb() && window.ua && window.ua.pc ? null : this.renderLeftButton(); // PC跳转到M站的售后页面不显示左侧返回箭头
    let closeWeb = null;
    if (this.props.renderCloseWeb) {
      closeWeb = this.props.renderCloseWeb();
    }
    return (
      <View
        style={[
          {
            backgroundColor: this.getTitleBackgourndColor(),
          },
          this.styles.navBarContainer,
          {
            width: '100%',
            height: CommonStyles.titleBar.height - 1,
          },
        ]}
      >
        <TouchableWithoutFeedback onPress={this.props.onTitlePress}>{this.renderTitleLayer()}</TouchableWithoutFeedback>
        <View style={[this.styles.navBarLeftContainer, { height: CommonStyles.titleBar.height - 1 }]}>
          {!!leftBtn && React.cloneElement(leftBtn, { accessibilityLabel: '返回' })}
          {!!closeWeb && React.cloneElement(closeWeb, { accessibilityLabel: '关闭' })}
        </View>
        <View style={[this.styles.navBarRightContainer, { height: CommonStyles.titleBar.height - 1 }]}>
          {this.renderRightButton()}
        </View>
      </View>
    );
  }

  renderTitleLayer() {
    if (this.props.renderCenter) {
      return this.props.renderCenter(this.props.transparent);
    }

    let w = CommonStyles.ViewWidth - 90;
    if (this.props.titleWidth) {
      w = this.props.titleWidth;
    }
    const padding = (CommonStyles.ViewWidth - w) / 2;
    let marginLeft = padding;
    let marginRight = padding;
    if (this.props.leftMargin) {
      marginLeft = padding - this.props.leftMargin;
      marginRight = padding + this.props.leftMargin;
    }
    if (this.props.leftMarginCustom) {
      marginLeft = this.props.leftMarginCustom;
    }
    if (this.props.rightMarginCustom) {
      marginRight = this.props.rightMarginCustom;
    }
    return (
      <View
        style={[
          {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            marginLeft: marginLeft,
            marginRight: marginRight,
            backgroundColor: 'rgba(0,0,0,0)',
          },
        ]}
      >
        {this.renderTitle(w)}
      </View>
    );
  }

  getTitleTextColor() {
    if (this.props.getTitleTextColor) {
      return this.props.getTitleTextColor(this.props.transparent);
    }

    if (this.props.useWhiteIcon) {
      return CommonStyles.white_80_transparent;
    }
    return '#333333';
  }

  getTitleBackgourndColor() {
    if (this.props.getTitleBackgourndColor) {
      return this.props.getTitleBackgourndColor(this.props.transparent);
    }

    if (this.props.transparent) {
      return 'rgba(0,0,0,0)';
    }
    return CommonStyles.titleBar.backgroundColor;
  }

  getDefaultBackImage(isPressed) {
    if (this.props.useWhiteIcon) {
      if (isPressed) {
        return ImageAssets.Icons.ArrowLeftLightBlack();
      }
      return ImageAssets.Icons.ArrowLeftCircleLightGray();
    }

    if (isPressed) {
      return ImageAssets.Icons.ArrowLeftBlack();
    }
    return ImageAssets.Icons.ArrowLeftBlack();
  }

  getDefaultMoreImage(isPressed) {
    if (this.props.useWhiteIcon) {
      if (isPressed) {
        return ImageAssets.Icons.MoreSolidGray2();
      }
      return ImageAssets.Icons.MoreLightGray();
    }

    if (isPressed) {
      return ImageAssets.Icons.MoreSolidGray();
    }
    return ImageAssets.Icons.MoreBorderGray();
  }

  renderStatusBar() {
    if (!this.props.transparent && CommonStyles.statusBarHeight > 0) {
      // 固定小程序中的statusBar的高度
      const miniStyleHeight = CommonBridge.isWxminiprogram() ? { height: CommonStyles.statusBarHeight } : {};
      /*
            在View里面包了一个无用的View，之所以这么做，是因为在安卓端的购物车页面，导航栏设置了透明度，statusBar和naviBar颜色却不一样。iOS端没问题。
            之前的statusBar只有一层View，而naviBar有多层View。加了一层View就好了，但解释不清楚。
             */
      return (
        <View style={[this.styles.statusBar, { width: CommonStyles.ViewWidth }, miniStyleHeight]}>
          <View style={[this.styles.statusBar, { width: CommonStyles.ViewWidth }, miniStyleHeight]} />
        </View>
      );
    }
    return null;
  }

  renderDefaultTitle(width) {
    const style = {
      fontSize: Scaledp(16),
      textAlign: 'center',
      textAlignVertical: 'center',
      color: this.getTitleTextColor(),
      fontWeight: '700',
    };
    if (width && width > 0) {
      style.width = width;
    }
    return (
      <Text style={style} numberOfLines={1}>
        {this.props.title}
      </Text>
    );
  }

  renderImageTitle(width) {
    const style = {
      height: 44,
      width: 91,
      overflow: 'hidden',
    };
    if (!style.width && width && width > 0) {
      style.width = width;
    }
    return <Image source={this.props.imageTitle} style={style} resizeMode={'contain'} />;
  }

  render() {
    this.styles = styles;
    let style = { backgroundColor: 'rgba(0,0,0,0)', marginTop: CommonStyles.statusBarHeight };
    if (!this.props.transparent) {
      if (this.props.showBottomSeparator) {
        style = {
          backgroundColor: CommonStyles.titleBar.backgroundColor,
        };
      } else {
        style = {
          backgroundColor: CommonStyles.titleBar.backgroundColor,
        };
      }
    }
    let zIndex = {};
    if (CommonBridge.isWeb()) {
      zIndex = {
        zIndex: 10,
      };
    }
    return System.isEmbeddedWebview() ? null : (
      <View
        style={[
          zIndex,
		  CommonStyles?.deviceModel === '24072PX77C' ? {}
          : {
            width: CommonStyles.ViewWidth,
          },
          CommonBridge.isWeb() ? style : {},
          this.props.style,
        ]}
      >
        {this.renderBackgroundImage()}
        {!CommonBridge.isWeb() && this.renderStatusBar()}
        <View style={[this.styles.container, style, { width: '100%' }, this.props.outerStyle]}>
          {this.renderNavbar()}
        </View>
      </View>
    );
  }
}
